import {
  AfterViewInit,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  ViewChild,
  Inject,
  inject,
  signal,
  effect,
  OnInit,
  computed,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';

import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import {
  SourceSelectionInjectionToken,
  SourceSelectionDependencies,
  SourceSelectionModalData,
  SelectedSourcesDisplayComponent,
  ReviewSourceSelectionModalService,
  MyListingSourceRenderImplemented,
} from '@vendasta/reviews';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { toSignal } from '@angular/core/rxjs-interop';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

interface ReviewSource {
  name: string;
  sourceId: string;
}
const SuggestedReviewSources: ReviewSource[] = [
  { name: 'EMAIL_BUILDER.REVIEW_REQUEST_LINKS_BLOCK.SOURCES.GOOGLE', sourceId: '10010' },
  { name: 'EMAIL_BUILDER.REVIEW_REQUEST_LINKS_BLOCK.SOURCES.FACEBOOK', sourceId: '10050' },
];

// Interface
export interface ReviewRequestLinksBlock {
  sources: string[];
  rawHtml: string;
}

const defaultSources = SuggestedReviewSources.map((source) => source.sourceId);

function renderedSourceOptionsHtml(sources: string[]): string {
  if (!sources || !sources.length || length === 0) {
    return '';
  }
  const htmlContent = sources
    .map((source) => `{% if reviewRequestCTAV2['${source}'] %} {{ reviewRequestCTAV2['${source}'] }} {% endif %}`)
    .join();
  return `<div> ${htmlContent} </div>`;
}

// Default blank value
export const blankReviewRequestLinksBlock: ReviewRequestLinksBlock = {
  sources: defaultSources,
  rawHtml: renderedSourceOptionsHtml(defaultSources),
};

@Component({
  selector: 'email-review-request-links-block',
  templateUrl: './review-request-links-block.component.html',
  styleUrls: ['./review-request-links-block.component.scss'],
  imports: [
    MatCheckboxModule,
    TranslateModule,
    GalaxyFormFieldModule,
    FormsModule,
    CommonModule,
    MatRadioModule,
    MatIconModule,
    MatButtonModule,
    SelectedSourcesDisplayComponent,
  ],
})
export class ReviewRequestLinksBlockComponent implements AfterViewInit, OnDestroy, OnInit {
  @HostBinding('class') class = 'email-review-request-links-block';
  @Input() content: ReviewRequestLinksBlock = blankReviewRequestLinksBlock;
  @ViewChild('blockInputs') blockInputs: NgForm;
  private productAnalyticsService = inject(ProductAnalyticsService);
  sourceSelectionLimit = 3;

  protected sourceSelectionModal = inject(ReviewSourceSelectionModalService, { optional: true });

  hasSourceSelection = false;
  defaultSourceInfo: ReviewSource[] = SuggestedReviewSources;

  activeSources = signal<string[]>(defaultSources);
  selectableSources = signal<string[]>([]);

  selectedSourceIDs = signal<string[]>(defaultSources);

  subscriptions: Subscription[] = [];

  constructor(
    private emailPreview: EmailPreviewRefreshService,
    private translateService: TranslateService,
    @Optional() @Inject(SourceSelectionInjectionToken) private sourceService?: SourceSelectionDependencies,
  ) {
    if (this.sourceService) {
      this.hasSourceSelection = true;
      this.setupSourceSelection();
    } else {
      this.hasSourceSelection = false;
    }
  }

  ngOnInit(): void {
    if (!this.hasSourceSelection) {
      return;
    }
    this.selectedSourceIDs.set(this.content.sources);
    this.productAnalyticsService.trackEvent('review-sources-block-added', 'email-template-create-activity', 'click');
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.blockInputs.form.valueChanges.subscribe(() => {
        this.emailPreview.refresh();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  isSourceSelected(sourceId: string): boolean {
    return !!this.content.sources.find((x) => x === sourceId);
  }

  valueChange(sourceId: string, $event): void {
    if ($event.checked) {
      this.content.sources.push(sourceId);
    } else {
      this.content.sources = this.content.sources.filter((x) => x !== sourceId);
    }
    this.content.rawHtml = renderedSourceOptionsHtml(this.content.sources);
    this.emailPreview.refresh();
  }

  // -------------- used with selection service --------
  setupSourceSelection(): void {
    const revSrc = toSignal(this.sourceService.reviewSources$, {
      initialValue: {
        primarySourceIds: ['10010', '10050'],
        otherSourceIds: [],
      },
    });
    const reviewSourceIDs = computed(() => {
      const sources = revSrc();
      return sources.primarySourceIds.concat(sources.otherSourceIds);
    });

    const urlValidSources = toSignal(this.sourceService.requestSources$, {
      initialValue: [],
    });

    const urlValidSourceIDs = computed(() => {
      return urlValidSources().map((source) => source.sourceId);
    });

    effect(() => {
      this.activeSources.set(reviewSourceIDs());
      this.selectableSources.set(urlValidSourceIDs());
    });
  }

  getValueFromModal(): void {
    if (this.sourceSelectionModal) {
      this.sourceSelectionModal
        .openModal({
          headerTranslationKey: 'EMAIL_BUILDER.REVIEW_REQUEST_LINKS_BLOCK.SOURCE_SELECTION_MODAL.HEADER',
          preselectedSources: this.selectedSourceIDs(),
          maxSelections: this.sourceSelectionLimit,
          idsOverrideForSelection: this.selectableSources(),
        } as SourceSelectionModalData)
        .subscribe((selectedSourceIDs: string[]) => {
          if (!selectedSourceIDs) {
            this.requestSourceIdsSelected(defaultSources);
            return;
          }
          this.requestSourceIdsSelected(selectedSourceIDs);
        });
    }
  }

  requestSourceIdsSelected(sourceIds: string[]): void {
    const selectedValidSources = this.selectableSources().filter((source) => sourceIds.includes(source));
    if (sourceIds.length && selectedValidSources.length > 0) {
      this.selectedSourceIDs.set(selectedValidSources);

      this.setContent(selectedValidSources);
    } else {
      this.selectedSourceIDs.set(defaultSources);
      this.setContent(defaultSources);
    }
  }

  setContent(contentSources = defaultSources): void {
    const sources = this.sortSourceIDs(contentSources);
    if (
      sources.length !== this.content.sources.length ||
      !sources.every((source) => this.content.sources.includes(source))
    ) {
      if (!MyListingSourceRenderImplemented) {
        const sourceIDs = sources.filter((source) => source !== '12000');
        this.content.sources = sourceIDs;
        this.content.rawHtml = renderedSourceOptionsHtml(sourceIDs);
      } else {
        this.content.sources = sources;
        this.content.rawHtml = renderedSourceOptionsHtml(sources);
      }
      this.emailPreview.refresh();
    }
  }

  sortSourceIDs(sourceIDs: string[]): string[] {
    const primaryIDsOrdered = ['10010', '10050', '12000'];
    const primaryIDs = sourceIDs
      .filter((id) => primaryIDsOrdered.includes(id))
      .sort((a, b) => {
        return primaryIDsOrdered.indexOf(a) - primaryIDsOrdered.indexOf(b);
      });
    const otherIDs = sourceIDs.filter((id) => !primaryIDsOrdered.includes(id));
    return primaryIDs.concat(otherIDs);
  }
}
